import { useCallback, useMemo, useState, type ReactElement } from 'react'
import { ArrowLeftIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import { Button } from 'app/components/ui/button'
import { Badge } from 'app/components/ui/badge'
import { SegmentedControl, type SegmentedOption } from 'app/components/action-center/SegmentedControl'
import { formatDate } from 'app/components/ops-datasets/mockData'
import { useOpsEvalsData } from './OpsEvalsContext'
import { useEvalsPanel } from './useEvalsPanel'
import { analyzeModel, type ModelAnalysis } from './analysis'
import { runLabels, isNoiseCheck, noiseMarginFor } from './runMeta'
import { VerdictPanel, VERDICT_LABEL } from './VerdictPanel'
import { MovementTable, type FieldFilter } from './MovementTable'
import { CallQueue, filterCalls, type CallFilter } from './CallQueue'
import { CallComparisonSheet } from './CallComparisonSheet'
import { PromptChanges } from './PromptChanges'
import { formatCost, formatTokens } from './pricing'
import { EXECUTION_LABEL, type Run } from './types'

function percentile(values: number[], share: number): number {
  const sorted = [...values].sort((a, b) => a - b)
  return sorted[Math.min(sorted.length - 1, Math.floor(share * sorted.length))] ?? 0
}

/** Tokens, cost and latency for each side, so a cheaper or faster pipeline shows. */
function CostStrip({
  analysis,
  simulated,
  labels,
}: {
  analysis: ModelAnalysis
  simulated: boolean
  labels: { candidate: string; baseline: string }
}): ReactElement {
  const results = analysis.items.map((entry) => entry.result)
  const sides = [
    { label: labels.candidate, usage: results.map(({ tokens, cost, latencyMs }) => ({ tokens, cost, latencyMs })) },
    { label: labels.baseline, usage: results.map((result) => result.baseline) },
  ].map(({ label, usage }) => {
    const cost = usage.reduce((sum, entry) => sum + entry.cost, 0)
    const latencies = usage.map((entry) => entry.latencyMs / 1000)
    return {
      label,
      tokens: formatTokens(usage.reduce((sum, e) => sum + e.tokens.promptTokens + e.tokens.completionTokens, 0)),
      cost: formatCost(cost),
      perCall: formatCost(usage.length ? cost / usage.length : 0),
      p50: `${percentile(latencies, 0.5).toFixed(1)}s`,
      p95: `${percentile(latencies, 0.95).toFixed(1)}s`,
    }
  })
  return (
    <section aria-label="Cost and speed" className="rounded-lg border border-default bg-surface-raised px-4 py-3">
      <div className="mb-2 text-caption-sm text-subtle">
        Cost and speed · {analysis.model}
        {simulated && ` · simulated values`}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[28rem] text-sm">
          <thead>
            <tr className="text-left text-caption-sm text-subtle">
              <th className="py-1 pr-4 font-normal">Side</th>
              <th className="py-1 pr-4 font-normal">Tokens</th>
              <th className="py-1 pr-4 font-normal">Cost</th>
              <th className="py-1 pr-4 font-normal">Per call</th>
              <th className="py-1 pr-4 font-normal">Latency p50</th>
              <th className="py-1 font-normal">p95</th>
            </tr>
          </thead>
          <tbody className="tabular-nums">
            {sides.map((side) => (
              <tr key={side.label} className="border-t border-subtle">
                <td className="py-1 pr-4 text-default">{side.label}</td>
                <td className="py-1 pr-4 font-medium text-strong">{side.tokens}</td>
                <td className="py-1 pr-4 font-medium text-strong">{side.cost}</td>
                <td className="py-1 pr-4 font-medium text-strong">{side.perCall}</td>
                <td className="py-1 pr-4 font-medium text-strong">{side.p50}</td>
                <td className="py-1 font-medium text-strong">{side.p95}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  )
}

export function RunDetail({ run }: { run: Run }): ReactElement {
  const { closeRun, selectItem, focusedItem, clearItem } = useEvalsPanel()
  const { runs, itemsFor } = useOpsEvalsData()
  const [activeModel, setActiveModel] = useState(run.modelResults[0]?.model)
  const [callFilter, setCallFilter] = useState<CallFilter>(`changed`)
  const [fieldFilter, setFieldFilter] = useState<FieldFilter | null>(null)

  const labels = runLabels(run)
  const noiseCheck = isNoiseCheck(run)
  const simulated = run.execution === `simulated`
  const baselineLabel = labels.baseline

  const analyses = useMemo(
    () =>
      run.modelResults.map((result) =>
        analyzeModel(result, run.stages, run.id, noiseMarginFor(run, result.model, runs).margin),
      ),
    [run, runs],
  )
  const analysis = analyses.find((entry) => entry.model === activeModel) ?? analyses[0]
  const margin = noiseMarginFor(run, analysis?.model ?? ``, runs)

  const datasetItems = itemsFor(run.datasetId)
  const itemCount = run.modelResults[0]?.items.length ?? 0
  const visible = analysis ? filterCalls(analysis.items, callFilter, fieldFilter) : []

  // The sheet steps through the list on screen; a call opened from a link that the
  // current filter hides steps through every call instead.
  const queue =
    focusedItem && !visible.some((entry) => entry.result.itemId === focusedItem.item.itemId)
      ? (analysis?.items ?? [])
      : visible
  const focusedIndex = focusedItem ? queue.findIndex((entry) => entry.result.itemId === focusedItem.item.itemId) : -1
  const focusedEntry = focusedIndex >= 0 ? queue[focusedIndex] : null

  const step = useCallback(
    (offset: 1 | -1) => {
      const next = queue[focusedIndex + offset]
      if (next && analysis) selectItem(analysis.model, next.result.itemId)
    },
    [queue, focusedIndex, analysis, selectItem],
  )

  const applyFieldFilter = (filter: FieldFilter | null) => {
    setFieldFilter(filter)
    if (filter) setCallFilter(`changed`)
  }

  const modelOptions: SegmentedOption<string>[] = analyses.map((entry) => ({
    id: entry.model,
    label: `${entry.model} · ${VERDICT_LABEL[entry.comparison.verdict]}`,
  }))

  return (
    <div className="space-y-5">
      <div>
        <Button variant="tertiary" size="sm" className="mb-2" onClick={closeRun}>
          <ArrowLeftIcon className="mr-1 h-4 w-4" /> All runs
        </Button>
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="text-xl font-semibold text-strong">{labels.title}</h3>
          {simulated ? (
            <Badge color="amber" label="Simulated scores" />
          ) : (
            <Badge color="blue" label={EXECUTION_LABEL[run.execution]} />
          )}
        </div>
        <p className="mt-1 text-sm text-subtle">
          {run.datasetName} · {itemCount} calls · {run.models.join(`, `)} ·{` `}
          {simulated ? `temp ${run.temperature}` : `model default temperature`} · {formatDate(run.startedAt)} · by{` `}
          {run.startedBy}
        </p>
        {datasetItems.length > 0 && datasetItems.length !== itemCount && (
          <p className="mt-2 flex items-center gap-1.5 text-caption-sm text-amber-800">
            <ExclamationTriangleIcon className="h-4 w-4 shrink-0" />
            The dataset has changed since this run ({itemCount} → {datasetItems.length} calls). Re-run to compare
            against its current state.
          </p>
        )}
      </div>

      {analyses.length > 1 && (
        <SegmentedControl options={modelOptions} value={analysis?.model} onSelect={setActiveModel} />
      )}

      {analysis && (
        <>
          <VerdictPanel
            analysis={analysis}
            candidateLabel={labels.candidate}
            baselineLabel={labels.baseline}
            sameSides={noiseCheck}
            simulated={simulated}
            marginSource={margin.source}
            allChangesUnstable={
              !noiseCheck &&
              margin.unstable.size > 0 &&
              analysis.items.some((entry) => entry.changes.length > 0) &&
              analysis.items
                .filter((entry) => entry.changes.length > 0)
                .every((entry) => margin.unstable.has(entry.result.itemId))
            }
          />
          <PromptChanges run={run} />
          <MovementTable
            analysis={analysis}
            candidateLabel={labels.candidate}
            baselineLabel={baselineLabel}
            filter={fieldFilter}
            onFilter={applyFieldFilter}
          />
          <CallQueue
            items={analysis.items}
            visible={visible}
            filter={callFilter}
            onFilter={setCallFilter}
            fieldFilter={fieldFilter}
            onClearFieldFilter={() => setFieldFilter(null)}
            onOpen={(itemId) => selectItem(analysis.model, itemId)}
            unstable={noiseCheck ? new Set() : margin.unstable}
          />
          <CostStrip analysis={analysis} simulated={simulated} labels={labels} />
          <CallComparisonSheet
            entry={focusedEntry}
            stages={run.stages}
            items={itemCount}
            position={focusedIndex + 1}
            count={queue.length}
            source={focusedEntry ? datasetItems.find((entry) => entry.id === focusedEntry.result.itemId) : undefined}
            candidateLabel={labels.candidate}
            baselineLabel={baselineLabel}
            onStep={step}
            onClose={clearItem}
          />
        </>
      )}
    </div>
  )
}
